import React, { useState, useEffect, useRef } from "react";
import * as d3Shape from "d3-shape";
import { useTranslation } from "react-i18next";
import usePremiumTrial from "../../../hooks/usePremiumTrial";
import useAuth from "../../../hooks/useAuth";

import { convertLanguageToField } from "../../../utils/functions";

import styles from "./sexRouletteWheel.module.css";

export default function SexRouletteWheel({
  colors,
  innerRadius,
  rewards = [],
  borderWidth,
  borderColor,
  width = 400,
  containerRef,
  buttonContainerRef,
  onStart = () => {},
  onFinished = () => {},
}) {
  const { t, i18n } = useTranslation();
  const oneTurn = 360;
  const numberOfSegments = rewards.length > 8 ? 8 : rewards.length;
  const [wheelPaths, setWheelPaths] = useState([]);
  const [started, setStarted] = useState(false);
  const [canClick, setCanClick] = useState(true);
  const [overshootRotation, setOvershootRotation] = useState(0);
  const [finalRotation, setFinalRotation] = useState(0);
  const [names, setNames] = useState([]);
  const { spendTrialCredit } = usePremiumTrial();
  const { user } = useAuth();

  const wheel = useRef(null);
  const namesScroll = useRef(null);

  let limitedRewards = [];
  let currentAngle = 0;
  let winner = 0;
  let lastArcIndex = numberOfSegments - 1;
  let previousNameTarget = 0;

  const angleBySegment = oneTurn / numberOfSegments;
  const angleOffset = angleBySegment / 2;

  useEffect(() => {
    prepareWheel();
    prepareNames();
  }, []);

  const makeWheel = () => {
    setFinalRotation(0);
    let data = [];
    for (let i = 0; i < numberOfSegments; i++) {
      data.push(1);
    }

    const arcs = d3Shape.pie()(data);

    const c = colors ?? ["#E15454", "#FF8282", "#D32C2C", "#A62853"];

    return arcs.map((arc, index) => {
      const instance = d3Shape
        .arc()
        .padAngle(0.0)
        .outerRadius(width / 2)
        .innerRadius(innerRadius || 10);

      return {
        path: instance(arc), // Passa o arcData ajustado para o arc()
        color: c[index % c.length],
        value: limitedRewards[index], // Usar o array limitado
        centroid: instance.centroid(arc), // Centroid também precisa do arcData ajustado
      };
    });
  };

  const Knob = () => {
    const knob = useRef(null);

    const animate = () => {
      if (started && knob?.current) {
        const matrix = getComputedStyle(wheel.current).getPropertyValue(
          "transform"
        );

        // Extract matrix values
        const matrixValues = matrix.match(/matrix\(([^)]+)\)/);
        if (!matrixValues) {
          return 0;
        }
        const values = matrixValues[1].split(", ").map(Number);
        const a = values[0];
        const b = values[1];

        // Calculate the rotation in degrees
        const deg = Math.atan2(b, a) * (180 / Math.PI);

        const currentArcIndex = getCurrentArcIndex(deg, numberOfSegments);

        if (currentArcIndex !== lastArcIndex) {
          lastArcIndex = currentArcIndex;

          // Scroll name
          const children = namesScroll?.current?.children;
          const currentChild = children[currentArcIndex];

          smoothScrollLeft(namesScroll.current, currentChild.offsetLeft, 100);

          knob?.current?.classList?.remove(styles.animate);

          setTimeout(() => {
            knob?.current?.classList?.add(styles.animate);
          }, 20);
        }
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);

    const getCurrentArcIndex = (rotation, totalSlices) => {
      const sliceAngle = oneTurn / totalSlices;
      let index = Math.floor((rotation % oneTurn) / sliceAngle);
      return index < 0 ? index + totalSlices : index; // Ensure it's always positive
    };

    return (
      <div ref={knob} className={`${styles.knob}`}>
        <div className={styles.drop}>
          <div className={styles.circle} />
          <div className={styles.tail} />
        </div>
      </div>
    );
  };

  const Wheel = () => {
    return (
      <div className="w-full h-full">
        <div className={styles.knobContainer}>
          <Knob />
        </div>
        <div>
          <svg
            className={`${styles.svgWheel} ${started ? styles.animate : ""}`}
            viewBox={`0 0 ${width} ${width}`}
            style={{
              transform: `rotateZ(calc(${finalRotation}deg - ${angleOffset}deg))`,
              "--offset": `${angleOffset}deg`,
              "--overshoot": `${overshootRotation}deg`,
              "--final": `${finalRotation}deg`,
            }}
            ref={wheel}
          >
            <g transform={`translate(${width / 2}, ${width / 2})`}>
              {wheelPaths.map((arc, i) => {
                const [x, y] = arc.centroid;
                const image = arc?.value?.imagem?.toString();

                const posX = x - 35;
                const posY = y - 55;

                const rotation = (i * oneTurn) / numberOfSegments + angleOffset;

                return (
                  <g key={`arc-${i}`}>
                    <path d={arc.path} strokeWidth={1} fill={arc.color} />
                    <g transform={`rotate(${rotation}, ${x}, ${y})`}>
                      <image
                        x={posX}
                        y={posY}
                        width={70}
                        height={70}
                        href={image}
                        preserveAspectRatio="xMidYMid meet"
                      />
                    </g>
                  </g>
                );
              })}
            </g>
          </svg>
        </div>
      </div>
    );
  };

  const prepareWheel = () => {
    limitedRewards =
      rewards.length > 8
        ? rewards.sort(() => Math.random() - 0.5).slice(0, 8)
        : rewards;

    setWheelPaths(makeWheel());
  };

  const handlePlay = () => {
    let allowed = true;
    if (!user?.isPremium) {
      allowed = spendTrialCredit("roleta");
    }

    if (!allowed) {
      return;
    }

    const duration = 3000;

    setStarted(true);
    setCanClick(false);

    winner = Math.floor(Math.random() * numberOfSegments);

    const baseRotation =
      360 - winner * (oneTurn / numberOfSegments) + 360 * (duration / 1000);

    setOvershootRotation(currentAngle + baseRotation + 15);
    setFinalRotation(currentAngle + baseRotation);
    onStart(wheelPaths[winner].value);

    setTimeout(() => {
      setStarted(false);
      setTimeout(() => {
        buttonContainerRef.current.classList.add("animate");
        setTimeout(() => {
          onFinished();
          setTimeout(() => {
            setCanClick(true);
            setWheelPaths([]);
            setNames([]);

            setTimeout(() => {
              prepareWheel();
              prepareNames();
            }, 200);
          }, 3000);
        }, 300);
      }, 300);
    }, duration);
  };

  const prepareNames = () => {
    const currentLanguage = i18n.language;
    const field = convertLanguageToField(currentLanguage, "titulo");

    const aux = rewards.map((reward) => reward[field]).reverse();

    setNames(aux);

    setTimeout(() => {
      namesScroll.current.scrollTo(namesScroll.current.scrollWidth, 0);
    }, 100);
  };

  const smoothScrollLeft = (element, target, duration) => {
    if (previousNameTarget < target) {
      element.scrollLeft = namesScroll.current.scrollWidth - previousNameTarget;
    }

    const start = element.scrollLeft;
    const change = target - start;
    const startTime = performance.now();

    previousNameTarget = target;

    function animateScroll(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1); // Clamp to 1 to prevent overshooting

      element.scrollLeft = start + change * easeInOutQuad(progress);

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    }

    function easeInOutQuad(t) {
      return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    }

    requestAnimationFrame(animateScroll);
  };

  const updateScrollEffects = () => {
    const containerRect = namesScroll?.current?.getBoundingClientRect();

    if (!containerRect) {
      return;
    }

    const containerCenter = containerRect.width / 2;

    for (let i = 0; i < namesScroll?.current?.children?.length; i++) {
      const item = namesScroll.current.children[i];
      const itemRect = item.getBoundingClientRect();
      const itemCenter = itemRect.left + itemRect.width / 2;
      const distance = Math.abs(containerCenter - itemCenter);

      // Normalize distance (closer = 0, further = 1)
      const maxDistance = containerRect.width / 2; // Max distance before full fade
      const progress = Math.min(distance / maxDistance, 1);

      // Apply transformations
      item.style.transform = `translateY(${progress * 20}px) scale(${
        1 - progress * 0.2
      })`; // Move down & scale down
    }

    requestAnimationFrame(updateScrollEffects);
  };

  requestAnimationFrame(updateScrollEffects);

  return (
    <div ref={containerRef} className={styles.container}>
      <div className={styles.names} ref={namesScroll}>
        {names.map((name, index) => {
          return (
            <div key={`${name}-${index}`}>
              <h2 className="font-extrabold text-2xl">{name}</h2>
            </div>
          );
        })}
      </div>

      <div className={styles.outerLinearBorder}>
        <div className={styles.linearBorder}>
          <img
            src={require("../../../assets/images/games/naughtyGame/border.png")}
            className={styles.border}
            alt="border"
          />

          <div
            className={styles.innerBorder}
            style={{
              borderWidth: borderWidth || 2,
              borderColor: borderColor || "#fff",
            }}
          >
            <Wheel />
            <div className={styles.innerBorderShadow}>
              <div></div>
            </div>
          </div>
        </div>
      </div>
      <div ref={buttonContainerRef} className={styles.buttonContainer}>
        <button
          disabled={!canClick}
          type="button"
          onClick={handlePlay}
          className="btn btn-brand rounded-xl w-full"
        >
          {t("lets")}
        </button>
      </div>
    </div>
  );
}
